<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <h1>Tablero de Programas</h1>
    </div>
    <p class="subtitulo">
      Estado de la oferta académica frente al Sistema de Aseguramiento de la Calidad.
      Los vencimientos y las alertas se calculan de las resoluciones; no se capturan a mano.
    </p>
    <div class="divider"></div>

    <!-- La institución encabeza el tablero: los programas son suyos, y su
         acreditación institucional es distinta de la de cada programa. -->
    <button v-if="institucion" class="ies" @click="fichaIes = true">
      <div class="ies-identidad">
        <h2>{{ institucion.nombre }}</h2>
        <p class="ies-meta">
          <span v-if="institucion.codigo_snies">SNIES {{ institucion.codigo_snies }}</span>
          <span v-if="institucion.caracter_academico">· {{ institucion.caracter_academico }}</span>
          <span v-if="institucion.sector">· {{ institucion.sector }}</span>
          <span v-if="institucion.municipio">· {{ institucion.municipio }}, {{ institucion.departamento }}</span>
        </p>
      </div>
      <span
        class="ies-acreditacion"
        :class="institucion.acreditacion_vigente ? 'ok' : 'no'"
        :title="institucion.acreditacion_vigente
          ? 'Acreditación institucional vigente'
          : 'La institución no está acreditada; es distinto de que lo estén sus programas'"
      >
        {{ institucion.acreditacion_vigente
          ? 'Acreditada en alta calidad'
          : 'Sin acreditación institucional' }}
      </span>
      <span class="ies-abrir">ver ficha ›</span>
    </button>

    <div v-if="cargando" class="info">Cargando tablero…</div>

    <template v-else-if="tablero">
      <!-- KPIs. Cada uno abre abajo la lista de programas que lo componen:
           un número suelto no deja verificar de dónde sale. -->
      <div class="kpis">
        <button class="kpi" :class="{ activo: detalle === 'todos' }" @click="abrir('todos')">
          <span class="kpi-valor">{{ tablero.kpis.total_programas }}</span>
          <span class="kpi-nombre">Programas activos</span>
          <span class="kpi-detalle">
            <span class="kpi-sub" @click.stop="abrir('pregrado')">{{ tablero.kpis.pregrado }} pregrado</span> ·
            <span class="kpi-sub" @click.stop="abrir('posgrado')">{{ tablero.kpis.posgrado }} posgrado</span>
          </span>
        </button>
        <button class="kpi" :class="{ activo: detalle === 'vigente' }" @click="abrir('vigente')">
          <span class="kpi-valor">{{ tablero.kpis.con_registro_vigente }}</span>
          <span class="kpi-nombre">Con registro vigente</span>
          <span class="kpi-detalle">de {{ tablero.kpis.total_programas }}</span>
        </button>
        <button class="kpi kpi-destacado" :class="{ activo: detalle === 'alta_calidad' }" @click="abrir('alta_calidad')">
          <span class="kpi-valor">{{ tablero.kpis.con_alta_calidad }}</span>
          <span class="kpi-nombre">Acreditados en alta calidad</span>
          <span class="kpi-detalle">{{ tablero.kpis.pct_alta_calidad }}% de la oferta</span>
        </button>
        <button class="kpi" :class="{ activo: detalle === 'primer_registro' }" @click="abrir('primer_registro')">
          <span class="kpi-valor">{{ tablero.kpis.en_primer_registro }}</span>
          <span class="kpi-nombre">En primer registro</span>
          <span class="kpi-detalle">nunca han renovado</span>
        </button>
      </div>

      <!-- Detalle del KPI abierto -->
      <div v-if="detalle" class="detalle-kpi">
        <div class="dk-cab">
          <h2 class="seccion dk-titulo">{{ tituloDetalle }}</h2>
          <span class="dk-conteo">{{ programasDelDetalle.length }} registro{{ programasDelDetalle.length === 1 ? '' : 's' }}</span>
          <button class="dk-cerrar" @click="detalle = null">✕</button>
        </div>
        <p class="dk-ayuda">{{ ayudaDetalle }}</p>

        <!-- Filtros. Con 49 registros, buscar a ojo no es opción. -->
        <div class="filtros">
          <input
            v-model="busqueda"
            class="filtro-input filtro-busqueda"
            placeholder="Buscar por programa, código SNIES o título…"
          />
          <select v-model="fCampus" class="filtro-input">
            <option value="">Todos los campus</option>
            <option v-for="c in opcionesCampus" :key="c" :value="c">{{ c }}</option>
          </select>
          <select v-model="fNivel" class="filtro-input">
            <option value="">Todos los niveles</option>
            <option v-for="n in opcionesNivel" :key="n" :value="n">{{ n }}</option>
          </select>
          <select v-model="fModalidad" class="filtro-input">
            <option value="">Toda modalidad</option>
            <option v-for="m in opcionesModalidad" :key="m" :value="m">{{ m }}</option>
          </select>
          <button v-if="hayFiltros" class="filtro-limpiar" @click="limpiarFiltros">Limpiar</button>
        </div>

        <div v-if="cargandoLista" class="info">Cargando programas…</div>
        <div v-else v-drag-scroll class="contenedor-tabla">
          <table class="lista-tabla">
            <thead>
              <tr>
                <th>SNIES</th><th>PROGRAMA</th><th>CAMPUS</th><th>MODALIDAD</th>
                <th>NIVEL</th><th>REGISTRO</th><th>ALTA CALIDAD</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in programasDelDetalle" :key="p.id_programa_snies">
                <td>{{ p.codigo_snies }}</td>
                <td>
                  <button class="link-programa" @click="abrirFicha(p)">
                    {{ p.programa?.nombre ?? '—' }}
                  </button>
                  <small v-if="p.titulo_otorgado" class="dk-titulo-otorgado">{{ p.titulo_otorgado }}</small>
                </td>
                <td>{{ p.campus?.nombre ?? p.municipio ?? '—' }}</td>
                <td>{{ p.modalidad ?? '—' }}</td>
                <td>{{ p.nivel_formacion ?? '—' }}</td>
                <td>
                  <span v-if="p.vence_registro" :class="['badge-bandera', p.bandera ? `sem-${p.bandera}` : '']">
                    vence {{ p.vence_registro }}
                  </span>
                  <span v-else class="dk-nd">sin resolución</span>
                </td>
                <td>
                  <span v-if="p.alta_calidad_vigente" class="badge-bandera sem-verde">
                    acreditado · vence {{ p.vence_alta_calidad }}
                  </span>
                  <span v-else class="dk-nd">—</span>
                </td>
              </tr>
              <tr v-if="!programasDelDetalle.length">
                <td colspan="7" class="mensaje-vacio">No hay programas en este grupo.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Semáforo -->
      <h2 class="seccion">Alertas tempranas</h2>
      <div class="semaforo">
        <button
          v-for="b in ['roja', 'amarilla', 'verde']"
          :key="b"
          :class="['sem-chip', `sem-${b}`, { activo: filtroBandera === b }]"
          @click="filtroBandera = filtroBandera === b ? null : (b as Bandera)"
        >
          <span class="sem-valor">{{ tablero.banderas[b as Bandera] }}</span>
          <span class="sem-texto">{{ leyenda[b] }}</span>
        </button>
        <div v-if="tablero.banderas.sin_dato" class="sem-chip sem-sin">
          <span class="sem-valor">{{ tablero.banderas.sin_dato }}</span>
          <span class="sem-texto">sin fecha para calcular</span>
        </div>
      </div>

      <p v-if="tablero.banderas.sin_dato" class="nota-sin-dato">
        Los que no tienen fecha de ejecutoria o vigencia <strong>no se cuentan como al día</strong>:
        no se puede saber cuándo vencen.
      </p>

      <!-- Lo que hay que atender, ordenado por urgencia -->
      <div v-drag-scroll class="contenedor-tabla">
        <table v-if="alertasFiltradas.length" class="lista-tabla">
          <thead>
            <tr>
              <th>SNIES</th><th>PROGRAMA</th><th>CAMPUS</th><th>MODALIDAD</th><th>VENCE</th><th>ESTADO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in alertasFiltradas" :key="a.id_programa_snies">
              <td>{{ a.codigo_snies }}</td>
              <td>{{ a.programa ?? '—' }}</td>
              <td>{{ a.campus ?? '—' }}</td>
              <td>{{ a.modalidad ?? '—' }}</td>
              <td>{{ a.vence ?? '—' }}</td>
              <td>
                <span :class="['badge-bandera', `sem-${a.bandera}`]">
                  {{ textoUrgencia(a.meses_para_vencer) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="mensaje-vacio">
          {{ filtroBandera ? 'No hay programas en ese estado.' : 'No hay alertas activas.' }}
        </p>
      </div>

      <!-- Matriz Campus × Nivel -->
      <h2 class="seccion">Campus por nivel de formación</h2>
      <div v-drag-scroll class="contenedor-tabla">
        <table class="lista-tabla">
          <thead>
            <tr>
              <th>CAMPUS</th>
              <th v-for="n in niveles" :key="n" class="col-num">{{ n }}</th>
              <th class="col-num">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(niv, campus) in tablero.matriz_campus_nivel" :key="campus">
              <td>{{ campus }}</td>
              <td v-for="n in niveles" :key="n" class="col-num">{{ niv[n] ?? '·' }}</td>
              <td class="col-num total">{{ tablero.por_campus[campus] ?? 0 }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>TOTAL</td>
              <td v-for="n in niveles" :key="n" class="col-num total">{{ totalNivel(n) }}</td>
              <td class="col-num total">{{ tablero.kpis.total_programas }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <h2 class="seccion">Por modalidad</h2>
      <div class="modalidades">
        <div v-for="(n, m) in tablero.por_modalidad" :key="m" class="mod-chip">
          <strong>{{ n }}</strong> {{ m }}
        </div>
      </div>
    </template>

    <p v-else class="mensaje-vacio">No se pudo cargar el tablero.</p>

    <!-- Ficha de la institución: la misma idea que la del programa, un nivel
         más arriba. -->
    <div v-if="fichaIes && institucion" class="ficha-overlay" @click.self="fichaIes = false">
      <div class="ficha">
        <div class="ficha-cab">
          <div>
            <h2>{{ institucion.nombre }}</h2>
            <p class="ficha-sub">{{ institucion.caracter_academico ?? 'Institución de educación superior' }}</p>
          </div>
          <button class="ficha-cerrar" @click="fichaIes = false">✕</button>
        </div>

        <div class="ficha-datos">
          <div v-for="d in datosInstitucion" :key="d.etiqueta" class="fd">
            <span class="fd-etiqueta">{{ d.etiqueta }}</span>
            <span class="fd-valor">{{ d.valor }}</span>
          </div>
        </div>

        <div class="ies-acred-detalle" :class="institucion.acreditacion_vigente ? 'ok' : 'no'">
          <template v-if="institucion.acreditacion_vigente">
            <strong>Acreditada en alta calidad</strong>
            <span v-if="institucion.resolucion_acreditacion"> · resolución {{ institucion.resolucion_acreditacion }}</span>
            <span v-if="institucion.vence_acreditacion_calculado"> · vence {{ institucion.vence_acreditacion_calculado }}</span>
          </template>
          <template v-else>
            <strong>Sin acreditación institucional.</strong>
            No es lo mismo que los programas acreditados:
            {{ tablero?.kpis.con_alta_calidad ?? 0 }} de {{ tablero?.kpis.total_programas ?? 0 }}
            programas sí la tienen.
          </template>
        </div>

        <h3 class="ficha-seccion">Directivos</h3>
        <table v-if="institucion.directivos?.length" class="lista-tabla">
          <thead>
            <tr><th>CARGO</th><th>NOMBRE</th><th>DEPENDENCIA</th><th>ACTO</th><th>DESDE</th><th>HASTA</th></tr>
          </thead>
          <tbody>
            <tr v-for="d in institucion.directivos" :key="d.id_directivo" :class="{ saliente: !d.vigente }">
              <td>{{ d.cargo }}</td>
              <td>{{ d.nombre_completo }}</td>
              <td>{{ (d as any).area?.nombre ?? '—' }}</td>
              <td>{{ d.acto_nombramiento ?? '—' }}</td>
              <td>{{ d.fecha_desde ?? '—' }}</td>
              <td>{{ d.fecha_hasta ?? 'en el cargo' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="mensaje-vacio">Sin directivos registrados.</p>

        <p class="ficha-pie">
          Para editar estos datos ve a
          <router-link :to="{ name: 'GestionAreas' }">Estructura Organizacional</router-link>
          y selecciona la institución.
        </p>
      </div>
    </div>

    <!-- Ficha del programa: lo registrado en la estructura organizacional
         más su registro ante el Ministerio, sin salir del tablero. -->
    <div v-if="fichaAbierta" class="ficha-overlay" @click.self="fichaAbierta = null">
      <div class="ficha">
        <div class="ficha-cab">
          <div>
            <h2>{{ fichaAbierta.programa?.nombre ?? 'Programa' }}</h2>
            <p class="ficha-sub">{{ fichaAbierta.titulo_otorgado ?? 'Sin título registrado' }}</p>
          </div>
          <button class="ficha-cerrar" @click="fichaAbierta = null">✕</button>
        </div>

        <div class="ficha-datos">
          <div v-for="d in datosFicha" :key="d.etiqueta" class="fd">
            <span class="fd-etiqueta">{{ d.etiqueta }}</span>
            <span class="fd-valor">{{ d.valor }}</span>
          </div>
        </div>

        <h3 class="ficha-seccion">Actos administrativos</h3>
        <table v-if="fichaAbierta.resoluciones?.length" class="lista-tabla">
          <thead>
            <tr><th>ACTO</th><th>NÚMERO</th><th>EJECUTORIA</th><th>VIGENCIA</th><th>VENCE</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in fichaAbierta.resoluciones" :key="r.id_resolucion">
              <td>{{ etiquetaTipoActo(r.tipo_acto) }}</td>
              <td>{{ r.numero ?? '—' }}</td>
              <td>{{ r.fecha_ejecutoria ?? '—' }}</td>
              <td>{{ r.vigencia_anios ? r.vigencia_anios + ' años' : '—' }}</td>
              <td>
                <span v-if="r.fecha_vencimiento" :class="['badge-bandera', r.bandera ? `sem-${r.bandera}` : '']">
                  {{ r.fecha_vencimiento }}
                </span>
                <span v-else class="dk-nd">no calculable</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="mensaje-vacio">Sin resoluciones registradas.</p>

        <p class="ficha-pie">
          Para editar este registro o agregar resoluciones, ve a
          <router-link :to="{ name: 'GestionAreas' }">Estructura Organizacional</router-link>
          y selecciona el programa.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Tablero de registros SNIES y su vigencia.
 *
 * El semáforo refleja el corte legal: crítico a ≤10 meses del vencimiento, que es
 * el plazo para radicar la renovación.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted } from 'vue'
import { getInstitucionService, type InstitucionSnies } from '@/services/InstitucionSniesService'
import {
  getTableroSniesService,
  listarProgramasSniesService,
  getProgramaSniesService,
  etiquetaTipoActo,
  type TableroSnies,
  type Bandera,
  type ProgramaSnies
} from '@/services/ProgramaSniesService'

const tablero = ref<TableroSnies | null>(null)
const institucion = ref<InstitucionSnies | null>(null)
const cargando = ref(false)
const filtroBandera = ref<Bandera | null>(null)

// ── Detalle de un KPI ────────────────────────────────────────────────
type Detalle = 'todos' | 'pregrado' | 'posgrado' | 'vigente' | 'alta_calidad' | 'primer_registro'

const detalle = ref<Detalle | null>(null)
const programas = ref<ProgramaSnies[]>([])
const cargandoLista = ref(false)

/**
 * La lista se pide UNA vez y se reparte aquí.
 *
 * Los criterios (registro vigente, alta calidad, primer registro) llegan ya
 * resueltos por el backend en cada registro; aquí solo se elige qué filas
 * mostrar. Reimplementar los criterios en el cliente haría que el número del
 * KPI y la lista que lo explica pudieran discrepar.
 */
async function abrir(cual: Detalle) {
  detalle.value = detalle.value === cual ? null : cual
  if (detalle.value === null || programas.value.length) return

  cargandoLista.value = true
  try {
    programas.value = await listarProgramasSniesService({ estado: 'Activo' })
  } finally {
    cargandoLista.value = false
  }
}

/** Los que componen el KPI abierto, antes de aplicar los filtros de búsqueda. */
const programasDelKpi = computed<ProgramaSnies[]>(() => {
  const lista = programas.value
  switch (detalle.value) {
    case 'pregrado': return lista.filter(p => p.nivel_academico === 'Pregrado')
    case 'posgrado': return lista.filter(p => p.nivel_academico === 'Posgrado')
    case 'vigente': return lista.filter(p => p.registro_vigente)
    case 'alta_calidad': return lista.filter(p => p.alta_calidad_vigente)
    case 'primer_registro': return lista.filter(p => p.en_primer_registro)
    case 'todos': return lista
    default: return []
  }
})

// ── Filtros de búsqueda ──────────────────────────────────────────────
const busqueda = ref('')
const fCampus = ref('')
const fNivel = ref('')
const fModalidad = ref('')

const hayFiltros = computed(() => !!(busqueda.value || fCampus.value || fNivel.value || fModalidad.value))

function limpiarFiltros() {
  busqueda.value = ''
  fCampus.value = ''
  fNivel.value = ''
  fModalidad.value = ''
}

/** Opciones sacadas de lo que hay cargado, no de una lista fija. */
function opcionesDe(campo: (p: ProgramaSnies) => string | null | undefined): string[] {
  return [...new Set(programas.value.map(campo).filter((v): v is string => !!v))].sort()
}
const opcionesCampus = computed(() => opcionesDe(p => p.campus?.nombre ?? p.municipio))
const opcionesNivel = computed(() => opcionesDe(p => p.nivel_formacion))
const opcionesModalidad = computed(() => opcionesDe(p => p.modalidad))

/** Sin tildes ni mayúsculas: buscar "criminologia" tiene que encontrar "Criminología". */
function normalizar(texto: string): string {
  return texto.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

const programasDelDetalle = computed<ProgramaSnies[]>(() => {
  const termino = normalizar(busqueda.value.trim())

  return programasDelKpi.value.filter(p => {
    if (fCampus.value && (p.campus?.nombre ?? p.municipio) !== fCampus.value) return false
    if (fNivel.value && p.nivel_formacion !== fNivel.value) return false
    if (fModalidad.value && p.modalidad !== fModalidad.value) return false
    if (!termino) return true

    return [p.programa?.nombre, p.codigo_snies, p.titulo_otorgado, p.nucleo_basico]
      .some(v => v && normalizar(String(v)).includes(termino))
  })
})

const tituloDetalle = computed(() => ({
  todos: 'Todos los programas activos',
  pregrado: 'Programas de pregrado',
  posgrado: 'Programas de posgrado',
  vigente: 'Con registro calificado vigente',
  alta_calidad: 'Acreditados en alta calidad',
  primer_registro: 'En primer registro'
}[detalle.value ?? 'todos']))

const ayudaDetalle = computed(() => ({
  todos: 'Cada fila es un registro ante el Ministerio: un mismo programa aparece una vez por campus y modalidad.',
  pregrado: 'Nivel académico Pregrado según el maestro del SNIES.',
  posgrado: 'Especializaciones, maestrías y doctorados.',
  vigente: 'Su resolución de registro calificado no ha vencido.',
  alta_calidad: 'Tienen resolución de acreditación vigente.',
  primer_registro: 'Operan bajo su primer registro: nunca han radicado una renovación.'
}[detalle.value ?? 'todos']))

const leyenda: Record<string, string> = {
  roja: 'crítico · menos de 12 meses',
  amarilla: 'advertencia · 12 a 24 meses',
  verde: 'al día · más de 24 meses'
}

/** Niveles presentes en la matriz, en el orden del SNIES. */
const niveles = computed<string[]>(() => {
  const orden = ['Formación técnica profesional', 'Tecnológico', 'Universitario',
    'Especialización tecnológica', 'Especialización universitaria', 'Maestría', 'Doctorado']
  const presentes = new Set<string>()
  for (const fila of Object.values(tablero.value?.matriz_campus_nivel ?? {})) {
    Object.keys(fila).forEach(n => presentes.add(n))
  }
  // Primero los del orden oficial que existan, después cualquier otro.
  return [
    ...orden.filter(n => presentes.has(n)),
    ...[...presentes].filter(n => !orden.includes(n))
  ]
})

const alertasFiltradas = computed(() =>
  (tablero.value?.alertas ?? []).filter(a => !filtroBandera.value || a.bandera === filtroBandera.value)
)

function totalNivel(nivel: string): number {
  let total = 0
  for (const fila of Object.values(tablero.value?.matriz_campus_nivel ?? {})) total += fila[nivel] ?? 0
  return total
}

// ── Ficha de la institución ──────────────────────────────────────────
const fichaIes = ref(false)

const datosInstitucion = computed(() => {
  const i = institucion.value
  if (!i) return []

  return [
    { etiqueta: 'Código SNIES', valor: i.codigo_snies ?? '—' },
    { etiqueta: 'NIT', valor: i.nit ?? '—' },
    { etiqueta: 'Carácter académico', valor: i.caracter_academico ?? '—' },
    { etiqueta: 'Naturaleza jurídica', valor: i.naturaleza_juridica ?? '—' },
    { etiqueta: 'Sector', valor: i.sector ?? '—' },
    { etiqueta: 'Principal o seccional', valor: i.principal_o_seccional ?? '—' },
    { etiqueta: 'Estado', valor: i.estado ?? '—' },
    { etiqueta: 'Domicilio', valor: [i.direccion, i.municipio, i.departamento].filter(Boolean).join(', ') || '—' },
    { etiqueta: 'Teléfono', valor: i.telefono ?? '—' },
    { etiqueta: 'Norma de creación', valor: i.norma_creacion ?? '—' },
    { etiqueta: 'Fecha de la norma', valor: i.fecha_norma ?? '—' },
    { etiqueta: 'Sitio web', valor: i.url ?? '—' }
  ]
})

// ── Ficha del programa ───────────────────────────────────────────────
const fichaAbierta = ref<ProgramaSnies | null>(null)

/**
 * Se pide el detalle al abrir aunque la fila ya venga cargada: el listado no
 * trae todos los campos de caracterización, y mostrar la ficha a medias sería
 * peor que esperar un momento.
 */
async function abrirFicha(p: ProgramaSnies) {
  fichaAbierta.value = p
  const completo = await getProgramaSniesService(p.id_programa_snies)
  if (completo) fichaAbierta.value = completo
}

/** Los datos de caracterización, en el orden en que se leen. */
const datosFicha = computed(() => {
  const f = fichaAbierta.value
  if (!f) return []

  return [
    { etiqueta: 'Código SNIES', valor: f.codigo_snies },
    { etiqueta: 'Código anterior ICFES', valor: f.codigo_anterior_icfes ?? '—' },
    { etiqueta: 'Campus', valor: f.campus?.nombre ?? f.municipio ?? '—' },
    { etiqueta: 'Modalidad', valor: f.modalidad ?? '—' },
    { etiqueta: 'Nivel académico', valor: f.nivel_academico ?? '—' },
    { etiqueta: 'Nivel de formación', valor: f.nivel_formacion ?? '—' },
    { etiqueta: 'Área de conocimiento', valor: f.area_conocimiento ?? '—' },
    { etiqueta: 'Núcleo básico', valor: f.nucleo_basico ?? '—' },
    { etiqueta: 'Créditos', valor: f.creditos ?? '—' },
    { etiqueta: 'Duración', valor: f.periodos_duracion ? `${f.periodos_duracion} periodos` : '—' },
    { etiqueta: 'Periodicidad', valor: f.periodicidad ?? '—' },
    { etiqueta: 'Ubicación', valor: [f.municipio, f.departamento].filter(Boolean).join(', ') || '—' },
    { etiqueta: 'En el SNIES desde', valor: f.fecha_registro_snies ?? '—' },
    {
      etiqueta: 'Años de funcionamiento',
      valor: f.anios_funcionamiento !== null && f.anios_funcionamiento !== undefined
        ? `${f.anios_funcionamiento} años` : '—'
    },
    { etiqueta: 'Estado', valor: f.estado ?? '—' },
    {
      etiqueta: 'Costo de matrícula',
      valor: f.costo_matricula ? `$ ${f.costo_matricula.toLocaleString('es-CO')}` : '—'
    }
  ]
})

/** Un vencido no es lo mismo que uno por vencer: se dice con todas las letras. */
function textoUrgencia(meses: number | null): string {
  if (meses === null) return 'sin fecha'
  if (meses < 0) return `vencido hace ${Math.abs(meses)} meses`
  return `vence en ${meses} meses`
}

onMounted(async () => {
  cargando.value = true
  try {
    tablero.value = await getTableroSniesService()
    institucion.value = await getInstitucionService()
  } finally {
    cargando.value = false
  }
})
</script>

<style scoped>
.formulario-modelo { background: white; padding: 2rem; border-radius: var(--radius-lg); flex: 1; }
.subtitulo { color: #5a6b7c; font-size: 0.9rem; margin: 0.3rem 0 0; }
.divider { height: 1px; background: #e0e0e0; margin: 1rem 0 1.5rem; }
.info { padding: 1rem 0; color: #555; }
.mensaje-vacio { color: #888; font-style: italic; padding: 1rem 0; }
.seccion { font-size: 1rem; color: #14507d; margin: 1.8rem 0 0.7rem; }

.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.9rem; }
.kpi {
  border: 1px solid #e4eaf0; border-radius: 10px; padding: 1rem;
  display: flex; flex-direction: column; gap: 0.15rem; background: #fbfdff;
  text-align: left; font: inherit; cursor: pointer;
}
.kpi:hover { border-color: #c6ddf7; background: #f5faff; }
.kpi.activo { outline: 2px solid #14507d; outline-offset: 1px; }
.kpi-sub { cursor: pointer; }
.kpi-sub:hover { text-decoration: underline; color: #0e69a0; }

.detalle-kpi {
  border: 1px solid #c6ddf7; background: #f7fbff;
  border-radius: 10px; padding: 1rem; margin-top: 1rem;
}
.dk-cab { display: flex; align-items: center; gap: 0.7rem; }
.dk-titulo { margin: 0; }
.dk-conteo { font-size: 0.78rem; color: #6b7280; }
.dk-cerrar {
  margin-left: auto; border: none; background: none;
  color: #6b7280; font-size: 1rem; cursor: pointer; line-height: 1;
}
.dk-ayuda { font-size: 0.78rem; color: #6b7280; margin: 0.3rem 0 0; }
.dk-titulo-otorgado { display: block; font-size: 0.72rem; color: #9aa5b1; }
.dk-nd { color: #9aa5b1; font-size: 0.8rem; }

/* Encabezado institucional */
.ies {
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  border: 1px solid #e4eaf0; border-left: 4px solid #14507d;
  border-radius: 10px; padding: 0.9rem 1.1rem; margin-bottom: 1.2rem; background: #fbfdff;
}
.ies-identidad h2 { margin: 0; font-size: 1.05rem; color: #14507d; }
.ies-meta { margin: 0.2rem 0 0; font-size: 0.8rem; color: #6b7280; display: flex; gap: 0.4rem; flex-wrap: wrap; }
.ies-acreditacion {
  margin-left: auto; border-radius: 999px;
  padding: 0.3rem 0.8rem; font-size: 0.78rem; font-weight: 600;
}
.ies-acreditacion.ok { background: #e7f6ec; color: #1d7a3a; }
.ies-acreditacion.no { background: #f0f2f5; color: #6b7280; }
.ies { width: 100%; text-align: left; font: inherit; cursor: pointer; }
.ies:hover { border-color: #c6ddf7; background: #f5faff; }
.ies-abrir { font-size: 0.76rem; color: #0e69a0; white-space: nowrap; }
.ies-acred-detalle { border-radius: 8px; padding: 0.6rem 0.8rem; font-size: 0.82rem; margin-bottom: 0.4rem; }
.ies-acred-detalle.ok { background: #e7f6ec; color: #1d7a3a; }
.ies-acred-detalle.no { background: #f5f7fa; color: #4a5b6b; }
.lista-tabla tr.saliente { opacity: 0.65; }

/* Filtros */
.filtros { display: flex; gap: 0.5rem; flex-wrap: wrap; margin: 0.8rem 0 0; }
.filtro-input {
  border: 1px solid #d6dee6; border-radius: 6px;
  padding: 0.4rem 0.6rem; font-size: 0.82rem; background: white;
}
.filtro-busqueda { flex: 1; min-width: 16rem; }
.filtro-limpiar {
  border: none; background: none; color: #0e69a0;
  font-size: 0.8rem; cursor: pointer; text-decoration: underline;
}

/* Nombre del programa: abre su ficha */
.link-programa {
  border: none; background: none; padding: 0; font: inherit;
  color: #0e69a0; cursor: pointer; text-align: left;
}
.link-programa:hover { text-decoration: underline; }

/* Ficha del programa */
.ficha-overlay {
  position: fixed; inset: 0; background: rgba(20, 32, 44, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 1.5rem; z-index: 50;
}
.ficha {
  background: white; border-radius: 12px; padding: 1.5rem;
  max-width: 60rem; width: 100%; max-height: 85vh; overflow-y: auto;
}
.ficha-cab { display: flex; align-items: flex-start; gap: 1rem; }
.ficha-cab h2 { margin: 0; font-size: 1.15rem; color: #14507d; }
.ficha-sub { margin: 0.2rem 0 0; font-size: 0.85rem; color: #6b7280; }
.ficha-cerrar {
  margin-left: auto; border: none; background: none;
  font-size: 1.1rem; color: #6b7280; cursor: pointer; line-height: 1;
}
.ficha-datos {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 0.7rem; margin: 1.2rem 0;
}
.fd { display: flex; flex-direction: column; gap: 0.1rem; }
.fd-etiqueta { font-size: 0.72rem; color: #9aa5b1; text-transform: uppercase; letter-spacing: 0.02em; }
.fd-valor { font-size: 0.88rem; color: #24303c; }
.ficha-seccion { font-size: 0.95rem; color: #14507d; margin: 1.2rem 0 0.5rem; }
.ficha-pie { margin-top: 1rem; font-size: 0.78rem; color: #6b7280; }
.kpi-destacado { border-color: #cfe6d4; background: #f4fbf6; }
.kpi-valor { font-size: 2rem; font-weight: 800; color: #14507d; line-height: 1; }
.kpi-destacado .kpi-valor { color: #1d7a3a; }
.kpi-nombre { font-size: 0.82rem; color: #374a5c; font-weight: 600; }
.kpi-detalle { font-size: 0.74rem; color: #9aa5b1; }

.semaforo { display: flex; gap: 0.7rem; flex-wrap: wrap; }
.sem-chip {
  border: 1px solid transparent; border-radius: 10px; padding: 0.6rem 0.9rem;
  display: flex; flex-direction: column; gap: 0.1rem; cursor: pointer; font: inherit; text-align: left;
}
.sem-chip.activo { outline: 2px solid #14507d; outline-offset: 1px; }
.sem-valor { font-size: 1.5rem; font-weight: 800; line-height: 1; }
.sem-texto { font-size: 0.72rem; }
.sem-roja { background: #fdecea; color: #b3261e; }
.sem-amarilla { background: #fff6e5; color: #9a6700; }
.sem-verde { background: #e7f6ec; color: #1d7a3a; }
.sem-sin { background: #f0f2f5; color: #6b7280; cursor: default; }
.nota-sin-dato { font-size: 0.78rem; color: #6b7280; margin: 0.6rem 0 0; }

.contenedor-tabla { width: 100%; overflow-x: auto; margin-top: 0.9rem; }
.lista-tabla { width: 100%; border-collapse: collapse; }
.lista-tabla th {
  text-align: left; padding: 0.55rem 0.7rem; background: #f5f7fa;
  font-size: 0.75rem; color: #4a5b6b; border-bottom: 1px solid #e0e0e0;
}
.lista-tabla td { padding: 0.5rem 0.7rem; border-bottom: 1px solid #eef2f6; font-size: 0.84rem; }
.lista-tabla tfoot td { font-weight: 700; background: #f5f7fa; }
.col-num { text-align: right; }
.total { font-weight: 700; color: #14507d; }
.badge-bandera { padding: 2px 8px; border-radius: 999px; font-size: 0.74rem; font-weight: 600; }

.modalidades { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.mod-chip {
  background: #eef5fb; color: #0e69a0; border-radius: 999px;
  padding: 0.35rem 0.8rem; font-size: 0.82rem;
}
</style>
